import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import { AssetType, SourceType } from "@nendaTypes/cms/CmsAssetType";
import { CMSAsset } from "@client/models/CMS/cmsAsset";
import {
  MIN_SEARCH_LENGTH,
  useCmsAssetSearch,
} from "@client/hooks/useCmsAssetSearch";
import PlayabilityBadge from "../AssetLibrary/PlayabilityBadge";

const SEARCH_FILTERS = {
  video: {
    type: [AssetType.Movie, AssetType.Episode],
    sourceType: [SourceType.vod],
  },
  channel: { type: [AssetType.Channel], sourceType: [SourceType.live] },
};

interface CmsAssetSearchProps {
  kind: keyof typeof SEARCH_FILTERS;
  onSelect: (asset: CMSAsset) => void;
}

const CmsAssetSearch: React.FC<CmsAssetSearchProps> = ({ kind, onSelect }) => {
  const [inputValue, setInputValue] = useState("");
  const { assets, isSearching } = useCmsAssetSearch(
    inputValue,
    SEARCH_FILTERS[kind]
  );
  const hasSearch = inputValue.trim().length >= MIN_SEARCH_LENGTH;

  return (
    <Autocomplete<CMSAsset>
      sx={{ maxWidth: "50ch" }}
      options={assets}
      value={null}
      inputValue={inputValue}
      onInputChange={(_event, value, reason) => {
        if (reason !== "reset") setInputValue(value);
      }}
      onChange={(_event, asset) => {
        if (!asset) return;
        onSelect(asset);
        setInputValue("");
      }}
      filterOptions={(options) => options}
      getOptionLabel={(asset) => asset.getDisplayName()}
      getOptionDisabled={(asset) => asset.playability?.playable === false}
      isOptionEqualToValue={(a, b) => a.assetId === b.assetId}
      loading={isSearching}
      noOptionsText={t(
        hasSearch
          ? "customerportal.pages.content_channels.asset_search.no_results"
          : "customerportal.pages.content_channels.asset_search.type_to_search"
      )}
      loadingText={t(
        "customerportal.pages.content_channels.asset_search.searching"
      )}
      renderOption={(props, asset) => (
        <Box component="li" {...props} key={asset.assetId}>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ overflowWrap: "anywhere" }}
            >
              {asset.getDisplayName()}
            </Typography>
            <Typography variant="caption" color="text.primary">
              {asset.assetId}
            </Typography>
            <PlayabilityBadge playability={asset.playability} />
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label={t("customerportal.pages.content_channels.asset_search.label")}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isSearching && <CircularProgress size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default CmsAssetSearch;
